import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import { Copy } from "lucide-react";

export interface DaySchedule {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

export interface ScheduleSettings {
  timezone: string;
  enabled?: boolean;
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
  [key: string]: any;
}

const browserTimezone = (): string => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
};

export const buildDefaultScheduleSettings = (
  overrides: Partial<ScheduleSettings> = {}
): ScheduleSettings => ({
  timezone: browserTimezone(),
  monday: { enabled: true, startTime: "09:00", endTime: "17:00" },
  tuesday: { enabled: true, startTime: "09:00", endTime: "17:00" },
  wednesday: { enabled: true, startTime: "09:00", endTime: "17:00" },
  thursday: { enabled: true, startTime: "09:00", endTime: "17:00" },
  friday: { enabled: true, startTime: "09:00", endTime: "17:00" },
  saturday: { enabled: false, startTime: "09:00", endTime: "17:00" },
  sunday: { enabled: false, startTime: "09:00", endTime: "17:00" },
  ...overrides,
});

const generateTimeOptions = () => {
  const options: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      const displayTime = new Date(
        `2000-01-01T${timeString}`
      ).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      options.push({ value: timeString, label: displayTime });
    }
  }
  return options;
};

const timezoneOptions = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "America/Alaska", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { value: "UTC", label: "UTC" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
];

const daysOfWeek = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

interface Props {
  value: ScheduleSettings;
  onChange: (next: ScheduleSettings) => void;
  title?: string;
  helperText?: string;
  /** Show a top-level "Enable Schedule" checkbox. When unchecked the rest
   *  of the UI collapses. Consumers that always want the schedule active
   *  (sequences, marketing campaigns) leave this off. */
  showEnableToggle?: boolean;
  /** Label shown next to the top-level enable checkbox. */
  enableToggleLabel?: string;
  /** Message shown when the top-level toggle is off. */
  disabledMessage?: string;
  /** When true, start >= end is treated as an overnight span (e.g.
   *  22:00–08:00) rather than auto-corrected. */
  allowOvernight?: boolean;
  disabled?: boolean;
  /** When true, wraps the content in a bordered card. Defaults to true for
   *  standalone use; set false when the parent already provides a card. */
  bordered?: boolean;
}

const SendScheduleEditor: React.FC<Props> = ({
  value,
  onChange,
  title = "Daily Schedule",
  helperText = "Actions will only be executed during the specified time windows on enabled days",
  showEnableToggle = false,
  enableToggleLabel = "Enable Schedule",
  disabledMessage = "Schedule disabled — runs 24/7",
  allowOvernight = false,
  disabled = false,
  bordered = true,
}) => {
  const [copyAnchorEl, setCopyAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [copySourceDay, setCopySourceDay] = useState<string | null>(null);
  const [selectedCopyDays, setSelectedCopyDays] = useState<string[]>([]);

  const timeOptions = generateTimeOptions();
  const isActive = !showEnableToggle || value?.enabled !== false;

  const handleEnabledToggle = () => {
    onChange({ ...value, enabled: !value.enabled });
  };

  const handleDayToggle = (day: string) => {
    onChange({
      ...value,
      [day]: {
        ...value[day],
        enabled: !value[day]?.enabled,
      },
    });
  };

  const handleTimeChange = (
    day: string,
    field: "startTime" | "endTime",
    nextValue: string
  ) => {
    const currentDay = value[day] || {
      enabled: false,
      startTime: "09:00",
      endTime: "17:00",
    };
    const next: ScheduleSettings = {
      ...value,
      [day]: {
        ...currentDay,
        [field]: nextValue,
      },
    };

    if (!allowOvernight) {
      const startTime =
        field === "startTime" ? nextValue : currentDay.startTime;
      const endTime = field === "endTime" ? nextValue : currentDay.endTime;

      if (startTime && endTime && startTime >= endTime) {
        if (field === "startTime") {
          const startMinutes =
            parseInt(startTime.split(":")[0]) * 60 +
            parseInt(startTime.split(":")[1]);
          const newEndMinutes = startMinutes + 15;
          const newEndHours = Math.floor(newEndMinutes / 60);
          const newEndMins = newEndMinutes % 60;
          if (newEndHours < 24) {
            next[day].endTime = `${newEndHours
              .toString()
              .padStart(2, "0")}:${newEndMins.toString().padStart(2, "0")}`;
          } else {
            next[day].endTime = "23:45";
          }
        } else {
          const endMinutes =
            parseInt(endTime.split(":")[0]) * 60 +
            parseInt(endTime.split(":")[1]);
          const newStartMinutes = Math.max(0, endMinutes - 15);
          const newStartHours = Math.floor(newStartMinutes / 60);
          const newStartMins = newStartMinutes % 60;
          next[day].startTime = `${newStartHours
            .toString()
            .padStart(2, "0")}:${newStartMins.toString().padStart(2, "0")}`;
        }
      }
    }

    onChange(next);
  };

  const handleTimezoneChange = (timezone: string) => {
    onChange({ ...value, timezone });
  };

  const handleCopyClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    day: string
  ) => {
    setCopyAnchorEl(event.currentTarget);
    setCopySourceDay(day);
    setSelectedCopyDays([]);
  };

  const handleCopyClose = () => {
    setCopyAnchorEl(null);
    setCopySourceDay(null);
    setSelectedCopyDays([]);
  };

  const handleCopyDayToggle = (day: string) => {
    setSelectedCopyDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleCopyToAll = () => {
    if (!copySourceDay) return;
    const sourceSettings = value[copySourceDay];
    const next: ScheduleSettings = { ...value };
    daysOfWeek.forEach((day) => {
      if (day.value !== copySourceDay) {
        next[day.value] = { ...sourceSettings };
      }
    });
    onChange(next);
    handleCopyClose();
  };

  const handleCopyToSelected = () => {
    if (!copySourceDay || selectedCopyDays.length === 0) return;
    const sourceSettings = value[copySourceDay];
    const next: ScheduleSettings = { ...value };
    selectedCopyDays.forEach((day) => {
      next[day] = { ...sourceSettings };
    });
    onChange(next);
    handleCopyClose();
  };

  const borderSx = bordered
    ? { p: 3, border: 1, borderColor: "divider", borderRadius: 2 }
    : {};

  const scheduleBody = (
    <>
      {!showEnableToggle && title && (
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "medium" }}>
          {title}
        </Typography>
      )}

      <Box sx={{ mb: 3 }}>
        <FormControl
          sx={{ minWidth: 300 }}
          disabled={disabled}
        >
          <InputLabel>Timezone</InputLabel>
          <Select
            value={value.timezone || browserTimezone()}
            label="Timezone"
            onChange={(e) => handleTimezoneChange(e.target.value as string)}
          >
            {timezoneOptions.map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormHelperText>
          All scheduled times are in the selected timezone
        </FormHelperText>
      </Box>

      {daysOfWeek.map((day) => {
        const dayVal = value[day.value];
        const isOvernight =
          allowOvernight &&
          dayVal?.enabled &&
          (dayVal?.startTime || "09:00") >= (dayVal?.endTime || "17:00");
        return (
          <Box
            key={day.value}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              mb: 2,
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: dayVal?.enabled ? "primary.light" : "divider",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={dayVal?.enabled || false}
                  onChange={() => handleDayToggle(day.value)}
                  disabled={disabled}
                />
              }
              label={day.label}
              sx={{
                minWidth: { xs: "auto", sm: 120 },
                mr: { xs: 0, sm: 3 },
                mb: { xs: dayVal?.enabled ? 2 : 0, sm: 0 },
              }}
            />

            {dayVal?.enabled && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", sm: "center" },
                  flex: 1,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <FormControl
                  size="small"
                  disabled={disabled}
                  sx={{
                    minWidth: { xs: "100%", sm: 120 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <InputLabel>Start</InputLabel>
                  <Select
                    value={dayVal?.startTime || "09:00"}
                    label="Start"
                    onChange={(e) =>
                      handleTimeChange(
                        day.value,
                        "startTime",
                        e.target.value as string
                      )
                    }
                  >
                    {timeOptions.map((option) => (
                      <MenuItem
                        key={`start-${day.value}-${option.value}`}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: { xs: "none", sm: "block" } }}
                >
                  to
                </Typography>
                <FormControl
                  size="small"
                  disabled={disabled}
                  sx={{
                    minWidth: { xs: "100%", sm: 120 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <InputLabel>End</InputLabel>
                  <Select
                    value={dayVal?.endTime || "17:00"}
                    label="End"
                    onChange={(e) =>
                      handleTimeChange(
                        day.value,
                        "endTime",
                        e.target.value as string
                      )
                    }
                  >
                    {timeOptions.map((option) => (
                      <MenuItem
                        key={`end-${day.value}-${option.value}`}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isOvernight && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    (overnight)
                  </Typography>
                )}
              </Box>
            )}

            {!dayVal?.enabled && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
              >
                No actions scheduled
              </Typography>
            )}

            {dayVal?.enabled && (
              <IconButton
                size="small"
                onClick={(e) => handleCopyClick(e, day.value)}
                disabled={disabled}
                sx={{ ml: { xs: 0, sm: 1 } }}
                title="Copy to other days"
              >
                <Copy size={16} />
              </IconButton>
            )}
          </Box>
        );
      })}

      <Popover
        open={Boolean(copyAnchorEl)}
        anchorEl={copyAnchorEl}
        onClose={handleCopyClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Copy schedule to:
          </Typography>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={handleCopyToAll}
            sx={{ mb: 1.5 }}
          >
            Copy to All Days
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Or select specific days:
          </Typography>
          {daysOfWeek
            .filter((day) => day.value !== copySourceDay)
            .map((day) => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedCopyDays.includes(day.value)}
                    onChange={() => handleCopyDayToggle(day.value)}
                  />
                }
                label={day.label}
                sx={{ display: "block", ml: 0 }}
              />
            ))}
          {selectedCopyDays.length > 0 && (
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleCopyToSelected}
              sx={{ mt: 1 }}
            >
              Copy to Selected
            </Button>
          )}
        </Box>
      </Popover>

      {helperText && (
        <FormHelperText sx={{ mt: 2 }}>{helperText}</FormHelperText>
      )}
    </>
  );

  return (
    <Box sx={bordered ? { mb: 2 } : {}}>
      {showEnableToggle && (
        <FormControlLabel
          control={
            <Checkbox
              checked={value?.enabled || false}
              onChange={handleEnabledToggle}
              disabled={disabled}
            />
          }
          label={<Typography variant="subtitle2">{enableToggleLabel}</Typography>}
          sx={{ mb: 1 }}
        />
      )}

      {isActive ? (
        <Box sx={borderSx}>{scheduleBody}</Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          {disabledMessage}
        </Typography>
      )}
    </Box>
  );
};

export default SendScheduleEditor;
